import Vue from 'vue';
import httpV2 from '@/utils/apiV2';
import Auth from '@/utils/auth';

const SCORING_TYPES = ['default', 'alt', 'whitelist'];
const DEFAULT_RETRY_DELAY = 1000;

/**
 * @typedef {Object} ScoringDomain
 * @property {string} name
 * @property {string} domain
 * @property {'default'|'alt'|'whitelist'} type
 */

const wait = delay => new Promise(resolve => setTimeout(resolve, delay));

const normalizeDomain = value => {
  const domain = String(value || '').trim().replace(/\/+$/, '');

  if (!domain) return '';
  if (/^https?:\/\//i.test(domain)) return domain;
  if (/^\/\//.test(domain)) return `https:${domain}`;

  return `https://${domain}`;
};

const getErrorStatus = error => error && error.response
  ? error.response.status
  : null;

const shouldRetry = error => {
  if (error && ['INVALID_SCORING_CONFIG', 'SCORING_CONFIG_CANCELLED'].includes(error.code)) return false;
  const status = getErrorStatus(error);
  return status === null || status >= 500;
};

const normalizeDomains = data => {
  if (!Array.isArray(data)) {
    const error = new Error('Scoring config response must be an array');
    error.code = 'INVALID_SCORING_CONFIG';
    throw error;
  }

  const domains = data.map(item => ({
    name: String(item && item.name || '').trim(),
    domain: normalizeDomain(item && item.domain),
    type: item && item.type,
  }));

  const isValid = domains.length > 0 && domains.every(item => (
    item.name && item.domain && SCORING_TYPES.includes(item.type)
  ));

  if (!isValid) {
    const error = new Error('Scoring config response is invalid');
    error.code = 'INVALID_SCORING_CONFIG';
    throw error;
  }

  return [
    ...domains.filter(item => item.type === 'default'),
    ...domains.filter(item => item.type !== 'default'),
  ];
};

class ConfigService {
  constructor() {
    this.state = Vue.observable({
      domains: [],
      loaded: false,
      loading: false,
      error: null,
      preloadAttempted: false,
    });
    this.pendingRequest = null;
    this.generation = 0;
  }

  get domains() {
    return this.state.domains;
  }

  get loaded() {
    return this.state.loaded;
  }

  get loading() {
    return this.state.loading;
  }

  get error() {
    return this.state.error;
  }

  get groups() {
    return SCORING_TYPES.reduce((result, type) => {
      result[type] = this.state.domains.filter(item => item.type === type);
      return result;
    }, {});
  }

  getByType(type) {
    return this.groups[type] || [];
  }

  async preload() {
    if (this.state.loaded) return this.state.domains;
    if (this.state.preloadAttempted) {
      return this.pendingRequest || this.state.domains;
    }

    this.state.preloadAttempted = true;
    return this.load({ attempts: 5 });
  }

  async load({ attempts = 1, retryDelay = DEFAULT_RETRY_DELAY, force = false } = {}) {
    if (this.state.loaded && !force) return this.state.domains;
    if (this.pendingRequest) return this.pendingRequest;

    const generation = this.generation;
    const request = this.loadWithRetry(attempts, retryDelay, generation);
    this.pendingRequest = request;

    try {
      return await request;
    } finally {
      if (this.pendingRequest === request) {
        this.pendingRequest = null;
      }
    }
  }

  async loadWithRetry(attempts, retryDelay, generation) {
    this.state.loading = true;
    this.state.error = null;

    try {
      for (let attempt = 1; attempt <= attempts; attempt += 1) {
        try {
          if (generation !== this.generation) {
            const error = new Error('Scoring config request was cancelled');
            error.code = 'SCORING_CONFIG_CANCELLED';
            throw error;
          }

          const response = await httpV2.get('/vue/config', {
            headers: {
              Authorization: Auth.getAuthHeader(),
            },
          });
          const domains = normalizeDomains(response.data);

          if (generation !== this.generation) {
            const error = new Error('Scoring config request was cancelled');
            error.code = 'SCORING_CONFIG_CANCELLED';
            throw error;
          }

          this.state.domains = domains;
          this.state.loaded = true;
          return domains;
        } catch (error) {
          if (attempt >= attempts || !shouldRetry(error)) {
            throw error;
          }
          await wait(retryDelay);
        }
      }
    } catch (error) {
      if (generation !== this.generation) throw error;
      this.state.domains = [];
      this.state.loaded = false;
      this.state.error = {
        status: getErrorStatus(error),
        message: error.message,
      };
      throw error;
    } finally {
      if (generation === this.generation) {
        this.state.loading = false;
      }
    }
  }

  clear() {
    this.generation += 1;
    this.state.domains = [];
    this.state.loaded = false;
    this.state.loading = false;
    this.state.error = null;
    this.state.preloadAttempted = false;
    this.pendingRequest = null;
  }
}

export { getErrorStatus };
export default new ConfigService();
